import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import type {Coach, ConferenceType, Strategy} from '../../types';

import {Modal} from '../../../../components/Modal';
import {logger} from '../../../../utils/logger';
import {useMobxStore} from '../../../../utils/store';
import {TestID} from '../../../../utils/test-utils/data-test-id';
import {BookConferenceStore} from '../../BookConference.store';
import {BookButton} from '../../components/BookButton';
import {Layout} from '../../components/Layout';
import {BookConferenceContainer} from '../../containers/BookConference.container';
import {IntroCallStrategy} from '../../IntroCallStrategy';
import {LiveSessionStrategy} from '../../LiveSessionStrategy';

export const BookConferenceModal = NiceModal.create<BookConferenceModal.Props>(
  ({
    coach,
    conferenceToRescheduleId,
    conferenceType,
    courseId,
    onBook,
    onCancel,
    strategy: propStrategy,
  }) => {
    const modal = useModal();

    const store = useMobxStore(() => {
      const strategy =
        propStrategy ??
        (coach
          ? new IntroCallStrategy(coach)
          : new LiveSessionStrategy(courseId));

      const bookingStore = new BookConferenceStore(strategy);

      if (conferenceType) {
        bookingStore.strategy.setConferenceType(conferenceType);
      }

      if (conferenceToRescheduleId) {
        bookingStore.strategy.setConferenceToRescheduleId(
          conferenceToRescheduleId,
        );
      }

      return bookingStore;
    });

    const onClose = () => {
      onCancel?.();

      modal.hide();
    };

    const onBookButtonClick = async () => {
      logger.event(
        store.strategy.conferenceToRescheduleId
          ? 'reschedule_session_tap'
          : 'schedule_session_tap',
      );

      try {
        await store.book();

        onBook?.();
      } catch (error) {
        // handled in store
      }
    };

    const renderFooter = () => (
      <Layout.BookButton.VerticalContainer>
        <Observer>
          {() => (
            <BookButton
              detail={store.bookingDateToString}
              disabled={!store.timeSlots.selected}
              isLoading={store.busy.getReason('book')}
              onClick={onBookButtonClick}
              testID={TestID.bookConference.button.confirm}
              title={store.title}
            />
          )}
        </Observer>
      </Layout.BookButton.VerticalContainer>
    );

    return (
      <Observer>
        {() => (
          <Modal
            isLoading={
              store.strategy.initialLoad.isLoading ||
              store.busy.getReason('book')
            }
            isOpen={modal.visible}
            onAfterClose={modal.remove}
            onRequestClose={onClose}
            renderFooter={renderFooter}
            title={() => (
              <Layout.Modal.TitleContainer>
                <Layout.Modal.Title>{store.title}</Layout.Modal.Title>
              </Layout.Modal.TitleContainer>
            )}
          >
            <BookConferenceContainer
              onBook={() => {
                store.strategy.init();

                onBook?.();
              }}
              store={store}
            />
          </Modal>
        )}
      </Observer>
    );
  },
);

export namespace BookConferenceModal {
  export type Props = {
    coach?: Coach;
    conferenceToRescheduleId?: string;
    conferenceType: ConferenceType;
    courseId: string;
    onBook?: () => void;
    onCancel?: () => void;
    strategy?: Strategy;
  };
}
